import { Protocol } from 'pmtiles';
import maplibregl, { type StyleSpecification } from 'maplibre-gl';

// ─── PMTiles protocol registration ────────────────────────────────────────────
// Kept for future self-hosted PMTiles migration (ADR-025).

let pmtilesRegistered = false;

export function ensurePMTilesProtocol(): void {
  if (pmtilesRegistered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile.bind(protocol));
  pmtilesRegistered = true;
}

// ─── Style builders ───────────────────────────────────────────────────────────

function buildSatelliteStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {
      satellite: {
        type: 'raster',
        // MapLibre does not support {s} subdomain notation — list all subdomains explicitly
        tiles: [
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        ],
        tileSize: 256,
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxzoom: 19,
      },
    },
    layers: [
      {
        id: 'satellite',
        type: 'raster',
        source: 'satellite',
      },
    ],
  };
}

function buildTopoStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {
      topo: {
        type: 'raster',
        // OpenTopoMap — 3 subdomains listed explicitly (no {s} support in MapLibre)
        tiles: [
          'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
          'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
          'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
        ],
        tileSize: 256,
        attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
        maxzoom: 17,
      },
    },
    layers: [
      {
        id: 'topo',
        type: 'raster',
        source: 'topo',
      },
    ],
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export type TileLayerKey = 'default' | 'satellite' | 'topo';

export interface MapStyle {
  name: string;
  // MapLibre accepts a URL string or a full StyleSpecification object
  style: string | StyleSpecification;
}

export const TILE_LAYERS: Record<TileLayerKey, MapStyle> = {
  // Full liberty style — includes road labels, place names, building footprints, etc.
  default:   { name: 'Map',       style: 'https://tiles.openfreemap.org/styles/liberty' },
  satellite: { name: 'Satellite', style: buildSatelliteStyle() },
  topo:      { name: 'Topo',      style: buildTopoStyle() },
};

export const TILE_LAYER_KEYS: TileLayerKey[] = ['default', 'satellite', 'topo'];
